import { Label } from "design-system/atoms/Label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { AddTagButton } from "modules/customers/components/Tags/AddTagButton"
import { TagBadge } from "modules/customers/components/Tags/TagBadge"
import { TagsPicker } from "modules/customers/components/Tags/TagsPicker"
import { useCreateTagsMutation } from "modules/customers/hooks/useCreateTagsMutation"
import { useTagsQuery } from "modules/customers/hooks/useTagsQuery"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { UpdateCustomerTagsOperationEnum, WorkflowNodeSchema } from "schema"
import z from "zod"
import { useWorkflowBuilder } from "../../hooks/useWorkflowBuilder"

type Operation = keyof typeof UpdateCustomerTagsOperationEnum

type Props = {
  nodeIndex: number
  node: Extract<z.infer<typeof WorkflowNodeSchema>, { type: "Action" }>
}

export function UpdateCustomerTagsConfiguration({ nodeIndex, node }: Props) {
  const { t } = useTranslation()
  const { form } = useWorkflowBuilder()
  const { data: companyTags = [] } = useTagsQuery()
  const { mutateAsync: createTags } = useCreateTagsMutation()

  useEffect(() => {
    if (node.config?.operation) return

    form.setValue(`nodes.${nodeIndex}.config`, {
      operation: UpdateCustomerTagsOperationEnum.Add,
      tags: [],
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const operation: Operation =
    form.watch(`nodes.${nodeIndex}.config.operation`) ?? UpdateCustomerTagsOperationEnum.Add
  const tags: string[] = form.watch(`nodes.${nodeIndex}.config.tags`) ?? []

  // Keeps a tag just created here in the list while the company tags refetch
  const companyTagNames = companyTags.map((tag) => tag.name)
  const newTagNames = tags.filter(
    (name) => !companyTagNames.some((tagName) => tagName.toLowerCase() === name.toLowerCase()),
  )
  const options = [...newTagNames, ...companyTagNames].map((name) => ({ value: name, label: name }))

  function setConfig(config: { operation: Operation; tags: string[] }) {
    form.setValue(`nodes.${nodeIndex}.config`, config, { shouldDirty: true })
    form.trigger(`nodes.${nodeIndex}.config`)
  }

  function toggle(name: string) {
    setConfig({
      operation,
      tags: tags.includes(name) ? tags.filter((tag) => tag !== name) : [...tags, name],
    })
  }

  async function create(name: string) {
    const [tag] = await createTags({ names: [name] })

    if (tag && !tags.includes(tag.name)) toggle(tag.name)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label>{t("campaigns.action.availableActions.UpdateCustomerTags.config.operation")}</Label>
        <Select
          value={operation}
          onValueChange={(value) => setConfig({ operation: value as Operation, tags })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(UpdateCustomerTagsOperationEnum).map((value) => (
              <SelectItem key={value} value={value}>
                {t(
                  `campaigns.action.availableActions.UpdateCustomerTags.config.operations.${value}`,
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>{t("campaigns.action.availableActions.UpdateCustomerTags.config.tags")}</Label>
        <div className="flex flex-wrap items-center gap-1.5">
          <TagsPicker options={options} selectedValues={tags} onToggle={toggle} onCreate={create}>
            <AddTagButton />
          </TagsPicker>
          {tags.map((name) => (
            <TagBadge key={name} name={name} onRemove={() => toggle(name)} />
          ))}
        </div>
      </div>
      {operation === UpdateCustomerTagsOperationEnum.Remove && (
        <p className="text-sm text-muted-foreground">
          {t("campaigns.action.availableActions.UpdateCustomerTags.config.removeDescription")}
        </p>
      )}
    </div>
  )
}
